import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Grid,
  Card,
  CardContent,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Delete,
  Visibility,
  Clear,
  Image,
  Person,
  Store,
  AccessTime,
  Notes,
  AcUnit,
  Schedule,
} from '@mui/icons-material';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import temperatureCaptureService from '../../services/temperatureCaptureService';
import storeService from '../../services/storeService';
import { TemperatureCapture, RoomName, Store as StoreType } from '../../types';

const TemperatureCapturesPage: React.FC = () => {
  const [captures, setCaptures] = useState<TemperatureCapture[]>([]);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [filters, setFilters] = useState({
    store_id: '',
    room_name: '' as RoomName | '',
    time_slot: '',
    capture_date: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    pages: 1,
  });
  const [selectedCapture, setSelectedCapture] = useState<TemperatureCapture | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const roomLabels: Record<RoomName, string> = {
    AMBIENT: 'Ambient',
    CHILLER: 'Chiller',
    FROZEN: 'Frozen',
  };

  useEffect(() => {
    fetchStores();
    fetchTimeSlots();
    fetchCaptures();
  }, []);

  useEffect(() => {
    fetchCaptures();
  }, [filters.page, filters.store_id, filters.room_name, filters.time_slot, filters.capture_date, filters.start_date, filters.end_date]);

  const fetchStores = async () => {
    try {
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (err: any) {
      console.error('Failed to fetch stores:', err);
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const slots = await temperatureCaptureService.getTimeSlots();
      setTimeSlots(slots);
    } catch (err: any) {
      console.error('Failed to fetch time slots:', err);
    }
  };

  const fetchCaptures = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: filters.page,
        limit: filters.limit,
      };
      
      if (filters.store_id) params.store_id = filters.store_id;
      if (filters.room_name) params.room_name = filters.room_name;
      if (filters.time_slot) params.time_slot = filters.time_slot;
      if (filters.capture_date) params.capture_date = filters.capture_date;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await temperatureCaptureService.getTemperatureCaptures(params);
      setCaptures(response.captures || []);
      setPagination(response.pagination || { total: 0, page: 1, limit: 20, pages: 1 });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch temperature captures');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    try {
      await temperatureCaptureService.deleteTemperatureCapture(deletingId);
      setSuccess('Temperature capture deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingId(null);
      fetchCaptures();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete temperature capture');
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const handleView = (capture: TemperatureCapture) => {
    setSelectedCapture(capture);
    setViewDialogOpen(true);
  };

  const handleClearFilters = () => {
    setFilters({
      store_id: '',
      room_name: '' as RoomName | '',
      time_slot: '',
      capture_date: '',
      start_date: '',
      end_date: '',
      page: 1,
      limit: 20,
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Temperature Captures
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Store</InputLabel>
            <Select
              value={filters.store_id}
              label="Store"
              onChange={(e) => setFilters({ ...filters, store_id: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Stores</MenuItem>
              {stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  {store.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Room</InputLabel>
            <Select
              value={filters.room_name}
              label="Room"
              onChange={(e) => setFilters({ ...filters, room_name: e.target.value as RoomName | '', page: 1 })}
            >
              <MenuItem value="">All Rooms</MenuItem>
              {Object.entries(roomLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Time Slot</InputLabel>
            <Select
              value={filters.time_slot}
              label="Time Slot"
              onChange={(e) => setFilters({ ...filters, time_slot: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Time Slots</MenuItem>
              {timeSlots.map((slot) => (
                <MenuItem key={slot} value={slot}>
                  {slot}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Date"
            type="date"
            value={filters.capture_date}
            onChange={(e) => setFilters({ ...filters, capture_date: e.target.value, page: 1 })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="Start Date"
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value, page: 1 })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="End Date"
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value, page: 1 })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearFilters}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Captures List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : captures.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No temperature captures found
          </Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {captures.map((capture) => (
              <Grid item xs={12} sm={6} md={4} key={capture.id}>
                <Card>
                  <CardContent>
                    {/* Photo */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img
                        src={capture.photo_url}
                        alt="Meter"
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Image+Not+Found';
                        }}
                      />
                    </Box>

                    {/* Room Name */}
                    <Chip
                      label={roomLabels[capture.room_name]}
                      color="primary"
                      sx={{ mb: 1 }}
                    />

                    {/* Time Slot */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <Schedule fontSize="small" color="action" />
                      <Typography variant="body2">{capture.time_slot}</Typography>
                    </Box>

                    {/* Meter Reading */}
                    {capture.meter_reading !== null && capture.meter_reading !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                        <AcUnit fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="bold">
                          {Number(capture.meter_reading).toFixed(2)}°C
                        </Typography>
                      </Box>
                    )}

                    {/* Store */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <Store fontSize="small" color="action" />
                      <Typography variant="body2">
                        {capture.store?.name || 'Unknown Store'}
                      </Typography>
                    </Box>

                    {/* Capture Date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <AccessTime fontSize="small" color="action" />
                      <Typography variant="body2">
                        {formatDate(capture.capture_date)} at {formatTime(capture.captured_at)}
                      </Typography>
                    </Box>

                    {/* Capturer */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <ProfileAvatar
                        imageUrl={capture.capturer?.profile_image_url}
                        firstName={capture.capturer?.first_name}
                        lastName={capture.capturer?.last_name}
                        sx={{ width: 24, height: 24, bgcolor: '#081122' }}
                      />
                      <Typography variant="body2">
                        {capture.capturer?.first_name} {capture.capturer?.last_name}
                        {capture.capturer?.employee_id && ` (${capture.capturer.employee_id})`}
                      </Typography>
                    </Box>

                    {/* Notes */}
                    {capture.notes && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, gap: 1 }}>
                        <Notes fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {capture.notes}
                        </Typography>
                      </Box>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleView(capture)}
                        fullWidth
                      >
                        View
                      </Button>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                          setDeletingId(capture.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} captures
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                disabled={pagination.page === 1}
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              >
                Previous
              </Button>
              <Button
                disabled={pagination.page >= pagination.pages}
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              >
                Next
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Temperature Capture Details</DialogTitle>
        <DialogContent>
          {selectedCapture && (
            <Box>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img
                  src={selectedCapture.photo_url}
                  alt="Meter"
                  style={{
                    width: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                  }}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Room</Typography>
                  <Typography variant="body1">{roomLabels[selectedCapture.room_name]}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Time Slot</Typography>
                  <Typography variant="body1">{selectedCapture.time_slot}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Capture Date</Typography>
                  <Typography variant="body1">{formatDate(selectedCapture.capture_date)}</Typography>
                </Grid>
                {selectedCapture.meter_reading !== null && selectedCapture.meter_reading !== undefined && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Meter Reading</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {Number(selectedCapture.meter_reading).toFixed(2)}°C
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Store</Typography>
                  <Typography variant="body1">{selectedCapture.store?.name || 'Unknown'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Captured At</Typography>
                  <Typography variant="body1">
                    {formatDate(selectedCapture.captured_at)} at {formatTime(selectedCapture.captured_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Captured By</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <ProfileAvatar
                      imageUrl={selectedCapture.capturer?.profile_image_url}
                      firstName={selectedCapture.capturer?.first_name}
                      lastName={selectedCapture.capturer?.last_name}
                      sx={{ width: 32, height: 32, bgcolor: '#081122' }}
                    />
                    <Typography variant="body1">
                      {selectedCapture.capturer?.first_name} {selectedCapture.capturer?.last_name}
                      {selectedCapture.capturer?.employee_id && ` (${selectedCapture.capturer.employee_id})`}
                    </Typography>
                  </Box>
                </Grid>
                {selectedCapture.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{selectedCapture.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Temperature Capture</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this temperature capture? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Box>
  );
};

export default TemperatureCapturesPage;

